import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Calendar } from 'lucide-mui';
import { useTranslation } from 'react-i18next';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { setDrawerState } from '../dashboardSlice';

interface EventsProps {
  open: boolean;
}
const Events: React.FC<EventsProps> = ({ open }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Check if the current route is the Events page
  const isActive = location.pathname === '/events';

  const handleEventsClick = () => {
    if (isMobile) {
      dispatch(setDrawerState('closed'));
    }
    navigate('/events');
  };

  return (
    <ListItem disablePadding sx={{ display: 'block' }}>
      <Tooltip title={t('drawer.events.title')} placement="right" arrow>
        <ListItemButton
          onClick={handleEventsClick}
          sx={{
            justifyContent: open ? 'initial' : 'center',
            px: open ? 2.5 : 1.25,
            borderRadius: 20,
            mx: 0.5,
            minHeight: '36px',
            position: 'relative',
            overflow: 'hidden',
            bgcolor: 'transparent',
            '&.Mui-selected': {
              bgcolor: 'transparent',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              backgroundColor: isActive
                ? theme.palette.action.hover
                : 'secondary.light',
              opacity: isActive ? 1 : 0,
              borderRadius: 'inherit',
            },
            '&:hover': {
              '&::before': {
                opacity: 1,
              },
            },
            '& > *': {
              position: 'relative',
              zIndex: 1,
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: open ? 3 : 'auto',
              justifyContent: 'center',
              pl: open ? 0 : 0.5,
              color: isActive ? 'inherit' : 'text.secondary',
              transition: theme.transitions.create(
                ['margin', 'padding', 'color'],
                {
                  duration: 150,
                  easing: theme.transitions.easing.easeOut,
                }
              ),
              '& svg': {
                color: 'inherit',
              },
            }}
          >
            <Calendar />
          </ListItemIcon>
          <ListItemText
            primary={t('drawer.events.title')}
            sx={{
              opacity: open ? 1 : 0,
              transition: theme.transitions.create(['opacity'], {
                duration: open ? 150 : 100,
                easing: theme.transitions.easing.easeOut,
                delay: open ? 50 : 0,
              }),
              '& .MuiTypography-root': {
                fontWeight: isActive ? 700 : 400,
                color: isActive ? 'text.primary' : 'text.secondary',
              },
            }}
          />
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );
};

export default Events;
